import { PlusCircleFilled, PlusOutlined } from '@ant-design/icons';
import { Button, Col, Dropdown, Input, Menu, Popover, Row, Select, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { produce } from 'immer';
import { TweenOneGroup } from 'rc-tween-one';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
	AiFillCheckCircle,
	AiFillLike,
	AiOutlineAim,
	AiOutlineFile,
	AiOutlineRedo,
	AiOutlineUnlock,
	AiOutlineUser,
	AiOutlineUserAdd
} from 'react-icons/ai';
import { useLocation, useNavigate } from 'react-router-dom';
import { Subscription } from 'rxjs';
import { useTheme } from 'styled-components';
import { ButtonType, TAButton, TAInput } from '../../../components';
import { AcpcMark } from '../../../components/acpc-mark/acpc-mark';
import ActionsMenu from '../../../components/base/data-display/actions-menu';
import DataDisplay from '../../../components/base/data-display/data-display-selector';
import { CandidateAvatar } from '../../../components/dashboard/candidates/candidate-avatar';
import CandidateViewModal from '../../../components/dashboard/candidates/candidate-view-modal';
import { MatchModalFormType } from '../../../components/dashboard/forms/match-modal-form';
import { RecommendCandidateModal } from '../../../components/dashboard/recommend-candidate-modal';
import { HighlightedDashboardSubtitle, SectionTitle, TASelect, TitleLink } from '../../../components/dashboard/styles';
import { PDFViewer } from '../../../components/pdf-viewer-modal/pdf-viewer-modal';
import { PSUTMark } from '../../../components/psut-mark/psut-mark';
import { useMedia, usePrevious } from '../../../hooks';
import candidatesService from '../../../services/candidates.service';
import companyService from '../../../services/company.service';
import jobsService from '../../../services/jobs.service';
import { TAResponse } from '../../../types';
import { CandidateType } from '../../../types/candidate-type';
import { getCandidateTypeFromCode } from '../../../utils';
import configService from '../../../utils/config';
import { formatCandidateIdentifierSearch } from '../../../utils/format-candidateIdentifier-search';
import { notifyError, notifySuccess } from '../../../utils/notification';
import { objToSearchFilter } from '../../../utils/obj-to-search-filter';
import { objectToSearchParams } from '../../../utils/object-to-searchparams';
import { cleanUndefined } from '../../../utils/objects';
import { parseParams } from '../../../utils/parse-params';
import { titlize } from '../../../utils/strings';
import classes from './candidates-list.module.css';

const tenantLogoMapper = {
	[CandidateType.ACPC]: <AcpcMark />,
	[CandidateType.PSUT]: <PSUTMark />
};

const TagComponent = ({ candidateId, setCandidateTags, candidateSource, cTags, compact }: any) => {
	const [tags, setTags] = useState(cTags || []);
	const [inputVisible, setInputVisible] = useState(false);
	const [inputValue, setInputValue] = useState('');
	const inputRef = useRef<any>();
	const theme = useTheme();
	useEffect(() => {
		if (inputVisible) {
			inputRef.current?.focus();
		}
	}, []);

	const handleClose = removedTag => {
		const newTags = tags.filter(tag => tag !== removedTag);
		setTags(newTags);
		setCandidateTags(candidateId, newTags, candidateSource);
	};

	const showInput = () => {
		setInputVisible(true);
	};

	const handleInputChange = e => {
		setInputValue(e.target.value);
	};

	const handleInputConfirm = () => {
		if (inputValue && tags.indexOf(inputValue) === -1) {
			setTags([...tags, inputValue]);
			setCandidateTags(candidateId, [...tags, inputValue], candidateSource);
		}
		setInputVisible(false);
		setInputValue('');
	};
	const tagsList = [
		'International jobs',
		'Star',
		'Assessed',
		'Remote only',
		'Freelancer',
		'Not recommended',
		'Managerial role',
		"Don't contact",
		'Corporate',
		'English',
		'CTO',
		'Good technically',
		'KSA',
		'Fintech',
		'ecommerce'
	];
	const forMap = tag => {
		const tagElem = (
			<Tag
				color="geekblue"
				className="d-flex align-items-center"
				closable
				onClose={e => {
					e.preventDefault();
					handleClose(tag);
				}}
			>
				{tag}
			</Tag>
		);
		return (
			<span
				key={tag}
				style={{
					display: 'inline-block'
				}}
			>
				{tagElem}
			</span>
		);
	};

	const tagChild = tags.map(forMap);
	return (
		<div className="d-flex align-items-center flex-column">
			<div className="my-1 d-flex align-items-center">
				<TweenOneGroup
					enter={{
						scale: 0.8,
						opacity: 0,
						type: 'from',
						duration: 100
					}}
					onEnd={(e: any) => {
						if (e?.type === 'appear' || e.type === 'enter') {
							e.target.style = 'display: inline-block';
						}
					}}
					leave={{
						opacity: 0,
						width: 0,
						scale: 0,
						duration: 200
					}}
					appear={false}
				>
					{compact ? (
						tagChild && tagChild.length ? (
							<Popover title="Tags" content={tagChild} trigger="click">
								<span style={{ color: theme.TA_GREEN }}>#Tags</span>
							</Popover>
						) : null
					) : (
						tagChild
					)}
				</TweenOneGroup>
			</div>
			{inputVisible && (
				<Input
					ref={inputRef}
					type="text"
					size="small"
					style={{
						width: 78
					}}
					autoFocus
					value={inputValue}
					onChange={handleInputChange}
					onBlur={handleInputConfirm}
					onPressEnter={handleInputConfirm}
					className="my-1 d-flex align-items-center"
				/>
			)}
			{!inputVisible && (
				<Dropdown
					className="d-flex align-items-center"
					trigger={['click']}
					overlay={
						<Menu>
							<Menu.Item onClick={showInput} className="site-tag-plus my-1 d-flex align-items-center">
								<PlusOutlined /> Add new
							</Menu.Item>
							{tagsList
								?.filter(name => !tags?.includes(name))
								?.map(tag => (
									<Menu.Item
										key={tag}
										onClick={e => {
											setCandidateTags(candidateId, [...tags, tag], candidateSource);
											setTags([...tags, tag]);
										}}
										className="site-tag-plus my-1 d-flex align-items-center"
									>
										{tag}
									</Menu.Item>
								))}
						</Menu>
					}
				>
					<Button
						icon={<PlusCircleFilled />}
						size="small"
						type="link"
						className="px-0"
						style={{ color: theme.TA_GREEN }}
					>
						Add Tag
					</Button>
				</Dropdown>
			)}
		</div>
	);
};

const InvitationStatusColorMap = {
	accepted: 'green',
	pending: 'geekblue',
	expired: 'volcano',
	notInvited: 'grey'
};

const { Option } = Select;
const CandidateListItemFactory = (viewCandidate, backOffice, viewDocument, allTags, setTags) => {
	const theme = useTheme();

	({ item }) => {
		return (
			<>
				<div className={classes.FirstColumn}>
					<CandidateAvatar
						link
						showAvatar={backOffice}
						candidate={item}
						to={`/dashboard/candidate/${item.id}${
							getCandidateTypeFromCode(item.code) === CandidateType.ACPC
								? '?source=acpc'
								: getCandidateTypeFromCode(item.code) === CandidateType.PSUT
								? '?source=psut'
								: ''
						}`}
						size={backOffice ? 50 : 32}
					/>
				</div>

				<div className={classes.SecondColumn}>
					{backOffice ? (
						<span
							onClick={() => viewCandidate(item)}
							color="#3F536E"
							className="py-2 d-inline-block"
							style={{ fontWeight: 500, cursor: 'pointer' }}
						>
							<span className="d-flex align-items-center">
								<TitleLink
									color="#3F536E"
									to={`/dashboard/candidate/${item.id}${
										getCandidateTypeFromCode(item.code) === CandidateType.ACPC
											? '?source=acpc'
											: getCandidateTypeFromCode(item.code) === CandidateType.PSUT
											? '?source=psut'
											: ''
									}`}
									size={15}
									className="d-inline-block me-1"
								>
									{`${item.firstName} ${item.lastName}`}
								</TitleLink>
								{tenantLogoMapper[getCandidateTypeFromCode(item.code)] ?? (
									<AiFillCheckCircle color={InvitationStatusColorMap[item.invitation?.status]} />
								)}
							</span>
						</span>
					) : null}
					<span
						style={{
							color: theme.TA_GREEN,
							whiteSpace: 'break-spaces',
							width: '70%',
							textAlign: 'center'
						}}
					>
						{item.title || 'No Job Title'}
					</span>
					<div className="d-flex justify-content-center">
						<TagComponent
							key={item.id}
							candidateId={item.id}
							candidateSource={getCandidateTypeFromCode(item.code)}
							cTags={item.tags}
							allTags={allTags}
							setCandidateTags={setTags}
							compact
						/>
					</div>
					{backOffice ? <span style={{ color: theme.TA_GREEN }}>{item.email}</span> : null}
					{backOffice && item.developerCv ? (
						<>
							<a
								href={item.cv}
								style={{ color: theme.TA_GREEN }}
								onClick={e => viewDocument(e, item.cv, item, 'cv')}
							>
								View CV
							</a>
							{backOffice ? (
								<span style={{ color: 'rgba(0,0,0,0.35)', display: 'block' }}>
									<i>Last updated: {dayjs(item.lastCvUpdate).format('MMMM D, YYYY')}</i>
								</span>
							) : null}
						</>
					) : item.cv ? (
						<>
							<a
								href={item.cv}
								style={{ color: theme.TA_GREEN }}
								onClick={e => viewDocument(e, item.cv, item, 'cv')}
							>
								View CV
							</a>
							{backOffice ? (
								<span style={{ color: 'rgba(0,0,0,0.35)', display: 'block' }}>
									<i>Last updated: {dayjs(item.lastCvUpdate).format('MMMM D, YYYY')}</i>
								</span>
							) : null}
						</>
					) : (
						<span>No CV</span>
					)}
				</div>
			</>
		);
	};
};

export const docTypeMapper = {
	cv: 'CV',
	report: 'Technical Report'
};
export const CandidatesList: React.FC<any> = ({ backOffice }) => {
	const [candidates, setCandidates] = useState<any>([]);
	const [jobs, setJobs] = useState<any>([]);
	const [companies, setCompanies] = useState<any>([]);
	const [display, setDisplay] = useState<any>([]);
	const [mobileDisplay, setMobileDisplay] = useState<any>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [showCandidateModal, setShowCandidateModal] = useState<boolean>(false);
	const [showRecommendModal, setShowRecommendModal] = useState<boolean>(false);
	const [forceApply, setForceApply] = useState<boolean>(false);
	const [candidateIdentifier, setCandidateIdentifier] = useState<any>(null);
	const [isLookingForJobs, setIsLookingForJobs] = useState<any>(null);
	const [tagsStatus, setTagsStatus] = useState<any>(null);
	const [selectedStatus, setSelectedStatus] = useState<any>(null);
	const [selectedCandidate, setSelectedCandidate] = useState<any>(null);
	const [selectedCandidateDocument, setSelectedCandidateDocument] = useState<any>(null);
	const [selectedDocumentType, setSelectedDocumentType] = useState<any>(null);
	const [pagination, setPagination] = useState<any>({});
	const [tags, setTags] = useState<any>([]);
	const [sortParams, setSortParams] = useState<any>({});
	const [candidatesFilter, setCandidatesFilter] = useState<any>({});
	const [hiredId, setHiredId] = useState<any>(null);
	const [displayCandidatesType, setDisplayCandidatesType] = useState<any>(CandidateType.TALENTS_ARENA);
	const [candidateIsLookingForJobs, setCandidateIsLookingForJobs] = useState<any>([]);

	const theme = useTheme();
	const mediaView = useMedia('md');
	const location = useLocation();
	const prevLocation = usePrevious(location);
	const navigate = useNavigate();

	const tableColumns = [
		{
			title: (
				<span className="d-inline-block p-1 text-center w-100" style={{ color: '#8DABC4' }}>
					Candidate
				</span>
			),
			dataIndex: 'avatar',
			key: 'avatar',
			fixed: 'left',
			render: (_, record) => (
				<span className="px-1 d-flex justify-content-center">
					<CandidateAvatar
						link
						showAvatar={backOffice}
						candidate={record}
						to={`/dashboard/candidate/${record.id}${
							getCandidateTypeFromCode(record.code) === CandidateType.ACPC
								? '?source=acpc'
								: getCandidateTypeFromCode(record.code) === CandidateType.PSUT
								? '?source=psut'
								: ''
						}`}
					/>
				</span>
			)
		},
		...(backOffice
			? [
					{
						title: (
							<span className="d-inline-block" style={{ color: '#8DABC4' }}>
								Name
							</span>
						),
						dataIndex: 'candidate',
						key: 'candidate',
						render: (candidate, record) => (
							<TitleLink
								color="#3F536E"
								to={`/dashboard/candidate/${record.id}${
									getCandidateTypeFromCode(record.code) === CandidateType.ACPC
										? '?source=acpc'
										: getCandidateTypeFromCode(record.code) === CandidateType.PSUT
										? '?source=psut'
										: ''
								}`}
								size={15}
								className="py-2 d-inline-block"
							>
								{`${record.firstName} ${record.lastName}`}{' '}
								{tenantLogoMapper[getCandidateTypeFromCode(record.code)] ?? null}
							</TitleLink>
						),
						sorter: { multiple: 1 }
					},
					{
						title: (
							<span className="d-inline-block" style={{ color: '#8DABC4' }}>
								Email
							</span>
						),
						dataIndex: 'email',
						key: 'email',
						width: '20%',
						render: (email, record) => <span style={{ color: theme.TA_GREEN }}>{email}</span>,
						sorter: { multiple: 1 }
					},
					{
						title: (
							<span className="d-inline-block py-1" style={{ color: '#8DABC4' }}>
								Registration
							</span>
						),
						dataIndex: 'invitation',
						key: 'invitation',
						render: (invitation, record) => {
							return (
								<Tag color={invitation ? InvitationStatusColorMap[invitation.status] : 'grey'}>
									{record.isRegistered
										? 'Registered'
										: invitation
										? titlize(invitation.status)
										: 'Not Invited'}
								</Tag>
							);
						}
					}
			  ]
			: []),
		{
			title: (
				<span className="d-inline-block" style={{ color: '#8DABC4' }}>
					Job Title
				</span>
			),
			width: backOffice ? '28%' : null,
			dataIndex: 'title',
			key: 'title',
			render: (title, candidateData) => (
				<div className="d-flex flex-wrap">
					<span className="d-flex w-100" style={{ color: theme.TA_GREEN }}>
						{title || 'NA'}
					</span>
					<TagComponent
						key={candidateData.id}
						candidateId={candidateData.id}
						candidateSource={getCandidateTypeFromCode(candidateData.code)}
						cTags={candidateData.tags}
						allTags={tags}
						setCandidateTags={setCandidateTags}
					/>
				</div>
			),
			sorter: { multiple: 1 }
		},
		{
			title: (
				<span className="d-inline-block" style={{ color: '#8DABC4' }}>
					CV
				</span>
			),
			dataIndex: ['backofficeCv', 'developerCv', 'cv'],
			key: 'cv',
			width: backOffice ? '15%' : '35%',
			render: (text, record) => {
				if (backOffice) {
					if (record.backofficeCv)
						return (
							<Tooltip title={`Last updated: ${dayjs(record.lastCvUpdate).format('MMMM D, YYYY')}`}>
								{renderCvViewButton(record.backofficeCv, record)}
							</Tooltip>
						);
					else if (record.developerCv)
						return (
							<Tooltip title={`Last updated: ${dayjs(record.lastCvUpdate).format('MMMM D, YYYY')}`}>
								{renderCvViewButton(record.developerCv, record)}
							</Tooltip>
						);
					else if (record.cv)
						return (
							<Tooltip title={`Last updated: ${dayjs(record.lastCvUpdate).format('MMMM D, YYYY')}`}>
								{renderCvViewButton(record.cv, record)}
							</Tooltip>
						);
					else return 'NA';
				} else if (record.cv) return renderCvViewButton(record.cv, record);
				else return 'NA';
			}
		},
		{
			title: (
				<span className="d-inline-block" style={{ color: '#8DABC4' }}>
					Actions
				</span>
			),
			dataIndex: 'actions',
			key: 'actions',
			render: (text, record) => {
				return (
					<span
						style={
							candidates?.length == 1 ? { minHeight: '20em', display: 'flex', alignItems: 'center' } : {}
						}
					>
						<ActionsMenu dotsOrientation="horizontal" options={actionsFactory(record)} />
					</span>
				);
			}
		}
	];

	const fetchCandidates = params => {
		return backOffice ? candidatesService.getCandidates(params) : companyService.getCompanyCandidates(null, params);
	};

	const searchObs$ = useMemo(
		() =>
			candidatesService.getPaginatedListFetcher({
				fetcher: v =>
					fetchCandidates({
						filter: v && v['filter'] ? objToSearchFilter(v['filter']) : null,
						pageSize: configService.defaultPageSize,
						sorters: sortParams,
						page: v && v['page'] ? v['page'] : null
					}),
				debounce: 700,
				distinct: true,
				beforeFetch: v => setCandidatesFilter(v && v['filter'] ? v['filter'] : {})
			}),
		[]
	);

	const sortObs$ = useMemo(
		() =>
			companyService.getPaginatedListFetcher({
				fetcher: ({ filter, sortInfo }) =>
					fetchCandidates({
						sorters: sortParams,
						pageSize: configService.defaultPageSize,
						filter
					}),
				debounce: 700,
				distinct: true,
				beforeFetch: ({ sortInfo }) => {
					setSortParams(sortInfo ? { ...sortInfo } : undefined);
					setLoading(true);
					setCandidates([]);
				}
			}),
		[]
	);

	const changePage$ = useMemo(
		() =>
			candidatesService.getPaginatedListFetcher({
				fetcher: fetchCandidates,
				debounce: false,
				distinct: false,
				beforeFetch: v => setLoading(true)
			}),
		[]
	);

	const subsribtions = new Subscription();

	const mountLogic = async () => {
		let candidates, pagination, candidatesTags, companies, selectedCandidate, showCandidateModal, jobs;
		subsribtions.add(
			searchObs$.subscribe(({ data: candidates, pagination }) => {
				setLoading(false);
				setCandidates(
					candidates.map(c => {
						c.avatarBgColor = '#4ADCAA';
						return c;
					})
				);
				setDisplay(candidates);
				setMobileDisplay([candidates]);
				setPagination(pagination);
			})
		);

		subsribtions.add(
			sortObs$.subscribe(({ data, pagination }) => {
				setLoading(false);
				setCandidates(data);
				setDisplay(data);
				setMobileDisplay([data]);
				setPagination(pagination);
			})
		);

		subsribtions.add(
			changePage$.subscribe(({ data: candidates, pagination }) => {
				const newCandidates = candidates.map(c => {
					c.avatarBgColor = '#4ADCAA';
					return c;
				});
				{
					setLoading(false);
					setCandidates(newCandidates);
					setDisplay(newCandidates);
					setMobileDisplay([...mobileDisplay, candidates]);
					setPagination(pagination);
				}
			})
		);

		let qs: any = {};
		if (location && location.search) {
			qs = parseParams(location.search);
			qs.page = +qs.page;
			qs.pageSize = +qs.pageSize;
		} else if (mediaView) {
			navigate({
				search: objectToSearchParams({ page: 1, pageSize: configService.defaultPageSize })
			});
		}

		try {
			[{ data: candidates, pagination }, { data: companies }, { data: jobs }, candidatesTags] = await Promise.all(
				[
					backOffice
						? candidatesService.getCandidates({
								page: qs.page ? qs.page : 1,
								pageSize: qs.pageSize ? qs.pageSize : configService.defaultPageSize
						  })
						: companyService.getCompanyCandidates(null, {
								page: qs.page ? qs.page : 1,
								pageSize: qs.pageSize ? qs.pageSize : configService.defaultPageSize
						  }),
					backOffice ? companyService.getCompanies() : Promise.resolve({ data: undefined }),
					jobsService.getJobs(null, null, true),
					backOffice ? candidatesService.getCandidatesTags() : Promise.resolve(undefined)
				]
			);
			//handle if no jobs are sent.
			if (!candidates || (backOffice && companies === null))
				throw new Error('Error while fetching data, Please try again later.');
		} catch (e: any) {
			notifyError(e.message);
			setLoading(false);
			return;
		}

		if (location && location.search) {
			const queryParams = {};
			if (backOffice && qs.candidateIdentifier) {
				queryParams['candidateIdentifier'] = qs.candidateIdentifier;
				setCandidateIdentifier(qs.candidateIdentifier);
			}

			if (qs.invitationStatus) {
				queryParams['invitationStatus'] = qs.invitationStatus;
				setSelectedStatus(qs.invitationStatus);
			}
			if (qs.isLookingForJobs) {
				queryParams['isLookingForJobs'] = qs.isLookingForJobs;
				setIsLookingForJobs(qs.isLookingForJobs);
			}
			if (qs.tagsStatus) {
				queryParams['tagsStatus'] = qs.tagsStatus;
				setTagsStatus(qs.tagsStatus);
			}
			if (qs.candidateType) {
				queryParams['candidateType'] = qs.candidateType;
				setDisplayCandidatesType(qs.candidateType);
			}
			const page = qs.page || 1;
			const pageSize = qs.pageSize || 10;

			if (Object.keys(queryParams).length) {
				searchObs$.next({ filter: queryParams, page, pageSize });
				navigate(objectToSearchParams({ ...queryParams, watched: qs.watched, page, pageSize }));
			}
		}
		candidates = candidates.map(c => {
			c.avatarBgColor = '#4ADCAA';
			return c;
		});

		setCandidates(candidates);
		setPagination(pagination);
		setCompanies(backOffice ? companies : []);
		setSelectedCandidate(selectedCandidate);
		setShowCandidateModal(showCandidateModal);
		setLoading(false);
		setDisplay(candidates);
		setMobileDisplay([candidates]);
		setJobs(jobs);
		setTags(candidatesTags);
	};

	useEffect(() => {
		mountLogic();
		return () => subsribtions.unsubscribe();
	}, []);

	const locationSearchChangeLogic = async () => {
		const newSearch = location.search;
		const oldSearch = prevLocation.search;
		if (!newSearch) {
			changePage$.next();
			setCandidatesFilter({});
		} else if (newSearch && oldSearch && newSearch !== oldSearch) {
			const newParams = parseParams(newSearch);
			const oldParams = parseParams(oldSearch);

			if (newParams.candidateType !== oldParams.candidateType && backOffice) {
				const tags = await candidatesService.getCandidatesTags(newParams.candidateType);
				setTags(tags);
			}

			const newFilter: any = cleanUndefined({
				tagsStatus: newParams.tagsStatus,
				isLookingForJobs: newParams.isLookingForJobs,
				invitationStatus: newParams.invitationStatus,
				candidateIdentifier: newParams.candidateIdentifier,
				candidateType: newParams.candidateType
			});
			const oldFilter: any = cleanUndefined({
				tagsStatus: oldParams.tagsStatus,
				isLookingForJobs: oldParams.isLookingForJobs,
				invitationStatus: oldParams.invitationStatus,
				candidateIdentifier: oldParams.candidateIdentifier,
				candidateType: oldParams.candidateType
			});

			if (Object.keys(newFilter).length) {
				if (
					newFilter.tagsStatus !== oldFilter.tagsStatus ||
					newFilter.isLookingForJobs !== oldFilter.isLookingForJobs ||
					newFilter.invitationStatus !== oldFilter.invitationStatus ||
					newFilter.candidateIdentifier !== oldFilter.candidateIdentifier ||
					newFilter.candidateType !== oldFilter.candidateType
				) {
					// fetchQueryCompany(newFilter, state.companies);
					setCandidatesFilter(newFilter);
					searchObs$.next({ filter: newFilter });
				}
			}
			if (
				(newParams.page && newParams.page !== oldParams.page) ||
				(newParams.pageSize && newParams.pageSize !== oldParams.pageSize)
			) {
				if (!mediaView) searchObs$.next({ filter: newFilter });
				else
					changePage$.next({
						page: newParams.page,
						pageSize: newParams.pageSize,
						filter: objToSearchFilter(newFilter),
						sorters: sortParams
					});
			}
		}
	};

	useEffect(() => {
		locationSearchChangeLogic();
	}, [location?.search]);

	const unLockJobApplication = async (applicationId, applicationPayload) => {
		try {
			const response = await jobsService.unLockJobApplication(applicationId, applicationPayload);
			if (!response.success) notifyError('Application Not Updated.');
			navigate({
				search: objectToSearchParams({ ...parseParams(location.search), unLockedAppId: applicationId })
			});
			setHiredId(applicationId);
		} catch (e: any) {
			notifyError(e.message);
			return;
		}
	};

	const lookingForJobs = async (candidateId, status) => {
		try {
			const response: TAResponse<any> = await candidatesService.lookingForJobs(
				candidateId,
				status,
				displayCandidatesType
			);
			if (!response.success) notifyError('Application Not Updated.');
			notifySuccess(`Candidate is successfully updated to looking for jobs`);
		} catch (e: any) {
			notifyError(e.message);
			return;
		}
	};

	const setCandidateTags = async (candidateId, tags, source) => {
		try {
			const response: TAResponse<any> = await candidatesService.setCandidateTags(candidateId, tags, source);
			if (!response.success) notifyError('Application Not Updated.');
			notifySuccess(`Candidate tags is successfully updated`);
		} catch (e: any) {
			notifyError(e.message);
			return;
		}
	};
	const viewCandidate = candidate => {
		if (!candidates) return;
		setSelectedCandidate(candidate);
		setShowCandidateModal(true);
	};

	const getCandidate = (candidateId, candiadtes = null) => {
		const candidate = (candiadtes || candidates).find(candidate => candidate.id === candidateId);
		if (!candidate) return null;
		return candidate;
	};

	const fetchJobs = params => {
		return jobsService.getJobs(params);
	};

	const hideCandidateViewModal = () => {
		setShowCandidateModal(false);
		setSelectedCandidate(null);
	};

	const renderCvViewButton = (text, record) => {
		return (
			<a href={text} style={{ color: theme.TA_GREEN }} onClick={e => viewDocument(e, text, record, 'cv')}>
				View
			</a>
		);
	};

	const handlePageChange = (page, pageSize) => {
		if (mediaView)
			navigate({
				search: objectToSearchParams({
					page,
					pageSize,
					...candidatesFilter
				})
			});
		// !selectedJob &&
		else
			changePage$.next({
				page,
				pageSize,
				filter: objToSearchFilter({ ...candidatesFilter }),
				sorters: sortParams
			});
	};

	const handleSearch = (e, key) => {
		let value;
		if (e && e.target && e.target.value !== undefined) value = e.target.value;
		else value = e;
		if (value === '') value = undefined;
		const filter = { ...candidatesFilter };
		filter[key] = value;
		if (filter.candidateIdentifier)
			filter.candidateIdentifier = formatCandidateIdentifierSearch(filter.candidateIdentifier);
		setCandidateIdentifier(filter['candidateIdentifier']);
		navigate(objectToSearchParams({ ...filter }));
		searchObs$.next({ filter });
	};

	const handleStatusSelect = val => {
		setLoading(true);
		const filter = val ? { status: val } : {};
		handleSearch(filter.status, 'invitationStatus');
		setLoading(false);
		setSelectedStatus(filter.status);
	};

	const handleLookingSelect = val => {
		setLoading(true);
		const filter = val ? { isLooking: val } : {};
		handleSearch(filter.isLooking, 'isLookingForJobs');
		setLoading(false);
		candidateIsLookingForJobs([]);
		setIsLookingForJobs(filter.isLooking);
	};
	const handleTagsSelect = val => {
		setLoading(true);
		const filter = val ? { selectedTags: val } : {};
		handleSearch(filter.selectedTags, 'tagsStatus');
		setLoading(false);
		setTagsStatus(filter.selectedTags);
	};

	const handleSort = sortInfo => {
		if (sortInfo !== undefined && mediaView)
			navigate({
				search: objectToSearchParams({ page: 1, pageSize: configService.defaultPageSize })
			});
		sortObs$.next({
			sortInfo,
			filter: objToSearchFilter({
				...candidatesFilter
			})
		});
	};

	const viewDocument = (e, selectedDocument, record, documentType) => {
		e.preventDefault();
		setSelectedCandidateDocument(selectedDocument);
		setSelectedCandidate(record);
		setSelectedDocumentType(documentType);
	};

	const recommendCandidate = (candidate, forceApply = false) => {
		if (!candidates) return;
		setSelectedCandidate(candidate);
		setShowRecommendModal(true);
		setForceApply(forceApply);
	};

	const onRecommendError = candidate => {
		if (!candidate) {
			console.error('Error while fetching data, Please try again later.');
			notifyError('Error while fetching data, Please try again later.');
		} else {
			notifyError(
				`Candidate ${candidate.firstName} ${candidate.lastName} is already recommended before for this job`
			);
		}
		setShowRecommendModal(false);
		setSelectedCandidate(null);
		return;
	};

	const onRecommendSuccess = (candidate, job) => {
		notifySuccess(
			`Candidate ${candidate.firstName} ${candidate.lastName} is successfully recommended to job ${job.title} for ${job.company.name} company`
		);

		const newDisplay = display.map(c => {
			if (c.id === candidate.id) c = candidate;
			return c;
		});
		setShowRecommendModal(false);
		setSelectedCandidate(null);
		setDisplay(newDisplay);
		setMobileDisplay([newDisplay]);
	};

	const matchForJobs = item => {
		const values = cleanUndefined({
			title: item.title,
			careerLevel: item.careerLevel ?? undefined,
			skills: item.skills && item.skills.length ? item.skills.map(s => s.name) : undefined,
			experience: item.yearsOfExp,
			type: MatchModalFormType.ctoj,
			keywords: item.keywords && item.keywords.length ? item.keywords.map(keyword => keyword.name) : undefined,
			minimumSalary: item.minimumSalary?.min ? item.minimumSalary.min : undefined
		});
		navigate({
			pathname: configService.URIs.matching_results_uri,
			search: objectToSearchParams(values)
		});
	};

	const inviteDeveloper = async candidate => {
		let resp;
		try {
			resp = await candidatesService.inviteDeveloper(candidate.email);
		} catch (e: any) {
			notifyError(e.message);
			return;
		}
		notifySuccess('Invitation sent successfully!');
		return resp.data;
	};

	const resendDeveloperInvitation = async candidate => {
		let resp;
		try {
			resp = await candidatesService.resendDeveloperInvitation(candidate.invitation?.id);
		} catch (e: any) {
			notifyError(e.message);
			return;
		}
		notifySuccess('Invitation Resent!');
		return resp.data;
	};

	const updateCandidate = (candidate, andSelect = true) => {
		const newState: any = {
			candiadtes: candidates.map(c => {
				if (c.id === candidate.id) {
					return candidate;
				}
				return c;
			})
		};
		if (andSelect) newState.selectedCandidate = candidate;
		setCandidates(newState.candiadtes);
		setDisplay(newState.candidates);
		setMobileDisplay([newState.candidates]);
		return;
	};

	const actionsFactory = candidate => {
		const actions = [
			{
				name: 'View Candidate',
				key: 'viewCandidate',
				icon: <AiOutlineUser />,
				action: () => viewCandidate(candidate)
			},
			...(backOffice
				? [
						{
							name: candidate.invitation ? 'Resend Invitation' : 'Invite',
							key: candidate.invitation ? 'resendInvitation' : 'invite',
							icon: candidate.invitation ? <AiOutlineRedo /> : <AiOutlineUserAdd />,
							disabled: candidate.isRegistered,
							action: async () => {
								const invitation = await (candidate.invitation
									? resendDeveloperInvitation(candidate)
									: inviteDeveloper(candidate));
								if (invitation)
									updateCandidate(
										produce(candidate, draft => {
											draft.invitation = invitation;
										}),
										false
									);
							}
						},
						{
							name: 'Recommend Candidate',
							key: 'recommendCandidate',
							icon: <AiFillLike />,
							disabled: candidate.hiredStatus?._id === hiredId ? false : candidate?.isLocked,
							tooltipText: `Hired ${dayjs(candidate.hiredStatus?.createdAt).format('MMMM D, YYYY')}`,
							action: () => recommendCandidate(candidate)
						},
						...((candidate.hiredStatus?._id === hiredId ? false : candidate?.isLocked)
							? [
									{
										name: 'Hired . Unlock',
										key: 'candidateLock',
										icon: <AiOutlineUnlock />,
										disabled: !candidate?.isLocked,
										tooltipText: `Hired ${dayjs(candidate.hiredStatus?.createdAt).format(
											'MMMM D, YYYY'
										)}`,
										action: () =>
											unLockJobApplication(candidate.hiredStatus?._id, { isAvailable: true })
									}
							  ]
							: []),
						...(candidate.isRegistered
							? [
									{
										name: 'Apply Candidate',
										key: 'applyCandidate',
										icon: <AiOutlineFile />,
										disabled: candidate.hiredStatus?._id === hiredId ? true : candidate?.isLocked,
										tooltipText: 'This will force the registered developer to apply for the job.',
										action: () => recommendCandidate(candidate, true)
									}
							  ]
							: []),
						{
							name: 'Match for Jobs',
							key: 'matchForJobs',
							icon: <AiOutlineAim />,
							tags: [
								{
									text: 'BETA',
									bgColor: '#f50',
									textColor: '#fff'
								}
							],
							action: () => matchForJobs(candidate)
						}
						// {
						// 	name: (
						// 		<span className="d-flex align-items-center">
						// 			Looking for jobs
						// 			<Switch
						// 				onClick={e => {
						// 					setCandidateIsLookingForJobs([
						// 						...candidateIsLookingForJobs?.filter(e => e.cId !== candidate.id),
						// 						{
						// 							cId: candidate.id,
						// 							status: e
						// 						}
						// 					]);
						// 				}}
						// 				checked={
						// 					candidateIsLookingForJobs?.find(e => e.cId == candidate.id)?.status !==
						// 					undefined
						// 						? candidateIsLookingForJobs?.find(e => e.cId == candidate.id)?.status
						// 						: !!candidate.isLookingForJobs
						// 				}
						// 				className="ms-2"
						// 			/>
						// 		</span>
						// 	),
						// 	key: 'isLooking',
						// 	icon: <AiOutlineSearch />,
						// 	tooltipText: `looking for jobs`,
						// 	action: () => lookingForJobs(candidate.id, !candidate.isLookingForJobs)
						// }
				  ]
				: [])
		];
		return actions;
	};

	const handleDisplayedCandidates = type => {
		setDisplayCandidatesType(type);
		handleSearch(type, 'candidateType');
	};

	return (
		<div className={`dashboard`} style={{ width: '95%' }} id="dashboard">
			<Row gutter={0} className="mb-3">
				<Col xs={24} className="text-start  d-flex flex-column mb-3 mb-md-0">
					<SectionTitle>Candidates</SectionTitle>
					<Col xs={24} md={8} className="mb-3 mb-md-0">
						You have{' '}
						<HighlightedDashboardSubtitle>{pagination.total} candidates</HighlightedDashboardSubtitle>
					</Col>
				</Col>
			</Row>
			<Row gutter={16} className="d-flex justify-content-end align-items-center mb-3">
				<Col xs={24} lg={{ span: 5 }} className="d-flex justify-content-center justify-content-lg-end">
					<Button.Group style={{ padding: '4px 0px', height: 'auto' }}>
						<TAButton
							btntype={
								displayCandidatesType === CandidateType.TALENTS_ARENA
									? ButtonType.PRIMARY
									: ButtonType.GHOST
							}
							onClick={() => handleDisplayedCandidates(CandidateType.TALENTS_ARENA)}
						>
							Talents Arena
						</TAButton>
						<TAButton
							btntype={
								displayCandidatesType === CandidateType.ACPC ? ButtonType.PRIMARY : ButtonType.GHOST
							}
							onClick={() => handleDisplayedCandidates(CandidateType.ACPC)}
						>
							ACPC
						</TAButton>
					</Button.Group>
				</Col>
				<Col xs={24} md={4} className="mb-3 mb-md-0">
					{!backOffice ? null : (
						<TASelect
							value={tagsStatus || undefined}
							onChange={handleTagsSelect}
							placeholder="Tags"
							showSearch={false}
						>
							{(tags || []).map(tag => (
								<Option value={tag} key={tag}>
									<Tag color="geekblue">{tag}</Tag>
								</Option>
							))}
						</TASelect>
					)}
				</Col>
				<Col xs={24} md={4} className="mb-3 mb-md-0">
					{!backOffice ? null : (
						<TASelect
							value={isLookingForJobs || undefined}
							onChange={handleLookingSelect}
							placeholder="Looking for a job"
							showSearch={false}
						>
							<Option key="yes" value="yes">
								<Tag color="green">{titlize('yes')}</Tag>
							</Option>
							<Option key="no" value="no">
								<Tag color="red">{titlize('no')}</Tag>
							</Option>
							<Option value="not_detected">
								<Tag color="warning">{titlize('Not Detected')}</Tag>
							</Option>
						</TASelect>
					)}
				</Col>
				{/* <Col xs={24} md={4} className="mb-3 mb-md-0">
						{!backOffice ? null : (
							<TASelect
								value={selectedStatus || undefined}
								onChange={handleStatusSelect}
								placeholder="Invitation status"
								showSearch={false}
							>
								{Object.keys(InvitationStatusColorMap).map(status => (
									<Option value={status} key={status}>
										<Tag color={InvitationStatusColorMap[status]}>{titlize(status)}</Tag>
									</Option>
								))}
							</TASelect>
						)}
					</Col> */}
				<Col xs={24} md={6} className="mb-3 mb-md-0">
					<TAInput
						value={candidateIdentifier || undefined}
						onChange={e => handleSearch(e, 'candidateIdentifier')}
						placeholder="Search Candidates"
						width="100%"
						className="mb-lg-0"
					/>
				</Col>
				{/* <Col xs={24} md={6} className="mb-3 mb-md-0">
						<TASelect
						ref={jobSelector}
						value={selectedJob || undefined}
						optionFilterProp="children"
						filterOption={(input, option) => {
							return (
								option.children[0].children[0].toLowerCase().indexOf(input.toLowerCase()) >= 0
								);
							}}
							onChange={handleJobSelect}
							placeholder="Select a job"
							style={{ width: '100%' }}
							className="ta-ant-input"
							showSearch
							onSearch={handleJobSearch}
							>
							{jobs.map(job => (
								<Option value={job._id} key={job._id}>
								<span className="me-2">{job.title} </span>
								<Tag color={JobStatusMap[job.status]}>{job.status}</Tag>
								</Option>
								))}
								</TASelect>
							</Col> */}
			</Row>
			<DataDisplay
				className="ta-table"
				data={display}
				mobileData={mobileDisplay}
				loading={loading}
				tableColumns={tableColumns}
				listItemComponent={CandidateListItemFactory(
					viewCandidate,
					backOffice,
					viewDocument,
					tags,
					setCandidateTags
				)}
				renderActions={actionsFactory}
				size="small"
				pagination={{ position: 'bottom', ...pagination }}
				onLoadMore={handlePageChange}
				onSort={handleSort}
			/>

			{selectedCandidate && showCandidateModal ? (
				<CandidateViewModal
					minimal={!backOffice}
					visible={showCandidateModal}
					candidate={selectedCandidate}
					onCandidateUpdate={updateCandidate}
					onCancel={hideCandidateViewModal}
					onEdit={() => {
						hideCandidateViewModal();
						// editCandidate(selectedCandidate);
					}}
					showCustomReports={!!backOffice}
					showApplicationsHistory={!!backOffice}
					candidateEditable={backOffice}
				/>
			) : null}

			{selectedCandidateDocument && (
				<PDFViewer
					url={selectedCandidateDocument}
					title={`${selectedCandidate.code}'s ${docTypeMapper[selectedDocumentType]}`}
					visible={!!selectedCandidateDocument}
					onCancel={() => {
						setSelectedCandidateDocument(null);
						setSelectedCandidate(null);
					}}
				/>
			)}

			{backOffice && selectedCandidate && showRecommendModal ? (
				<RecommendCandidateModal
					visible={showRecommendModal}
					candidate={selectedCandidate}
					forceApply={forceApply}
					onSuccess={onRecommendSuccess}
					onError={onRecommendError}
					onCancel={() => {
						setSelectedCandidate(null);
						setShowRecommendModal(false);
					}}
				/>
			) : null}
		</div>
	);
};
